// ===========================================
// CRM Notifications Hook (Phase 8)
// ===========================================
// Real-time listener for CRM notifications using Firestore onSnapshot.

import { useState, useEffect } from 'react';
import { onSnapshot, query, where, orderBy, limit, doc, updateDoc, writeBatch, getDocs } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { notificationsCollection } from '@/config/collections';
import type { CRMNotification } from '@/types';

function docToNotification(docSnap: any): CRMNotification {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    userId: data.userId,
    type: data.type,
    projectId: data.projectId,
    companyId: data.companyId,
    companyName: data.companyName,
    message: data.message,
    read: data.read || false,
    actionUrl: data.actionUrl,
    createdAt: data.createdAt?.toDate() || new Date(),
  };
}

export function useNotifications(userId: string | undefined) {
  const [notifications, setNotifications] = useState<CRMNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    // Simple query: just filter by userId, sort client-side
    // This avoids composite index requirements that may not be deployed yet
    const colRef = notificationsCollection(db);
    const q = query(
      colRef,
      where('userId', '==', userId),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs
          .map(docToNotification)
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
          .slice(0, 50);
        setNotifications(items);
        setLoading(false);
      },
      (error) => {
        // Gracefully handle permission errors (e.g., index not ready)
        // Don't break the UI - just show empty notifications
        console.warn('[useNotifications] Listener error (non-fatal):', error.message);
        setNotifications([]);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [userId]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markRead = async (notificationId: string) => {
    try {
      const colRef = notificationsCollection(db);
      const docRef = doc(colRef, notificationId);
      await updateDoc(docRef, { read: true });
    } catch (err) {
      console.warn('[useNotifications] Failed to mark read:', err);
    }
  };

  const markAllRead = async () => {
    if (!userId) return;
    const unread = notifications.filter((n) => !n.read);
    if (unread.length === 0) return;

    try {
      const batch = writeBatch(db);
      const colRef = notificationsCollection(db);
      for (const n of unread) {
        batch.update(doc(colRef, n.id), { read: true });
      }
      await batch.commit();
    } catch (err) {
      console.warn('[useNotifications] Failed to mark all read:', err);
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    markRead,
    markAllRead,
  };
}
